import {
    Dispatch,
    forwardRef,
    SetStateAction,
    useEffect,
    useImperativeHandle,
    useState,
} from 'react'
import style from './style.m.less'
import { useTranslation } from 'react-i18next'

import { CloseOutlined, QuestionCircleOutlined } from '@ant-design/icons'
import { Alert, Divider, Form, Input, Select, Spin, Tooltip, Typography, message } from 'antd'
import {
    AgentData,
    CalcResultResponse,
    CalculatorAuditResponse,
    CalculatorAuditValues,
    CalculatorDropdownData,
    CalculatorFormValues,
    CalcElectricityReportRequest,
    FormHandle,
} from '../../types'
import { formatNumber, restrictInput } from '../../helpers'
import { AgentsModal, ButtonsBlock } from '../index'
import request from '../../../../services/request'

const { Text } = Typography

interface IEnergyForm {
    data: CalculatorDropdownData | null
    auditId: number
    error: string
    resCalculate: CalcResultResponse
    isLoadingAuditUpdate: boolean
    resCalculatorAudit: CalculatorAuditResponse

    setError: Dispatch<SetStateAction<string>>
    scrollToMain: () => void
    auditUpdate: (auditData: CalculatorAuditValues) => void
    updateAuditId: (values: any, onSuccess: (values: any, data: any) => void) => void
    fetchCalculate: (body: any, onSuccess: () => void) => void
    resetAudit: () => void
}

const EnergyForm = forwardRef<FormHandle, IEnergyForm>(
    (
        {
            data,
            auditId,
            error,
            resCalculate,
            isLoadingAuditUpdate,
            setError,
            scrollToMain,
            auditUpdate,
            updateAuditId,
            fetchCalculate,
            resetAudit,
        },
        ref,
    ) => {
        const { t, i18n } = useTranslation()
        const locale = i18n.language.includes('ru') ? 'ru' : 'en'

        const [agentsModalDisplay, setAgentsModalDisplay] = useState(false)
        const [agentsData, setAgentsData] = useState<AgentData[]>([])

        const [calcForm] = Form.useForm()
        const [loading, setLoading] = useState(false)
        const [isFirstCalc, setIsFirstCalc] = useState(true)

        const [currentRegionId, setCurrentRegionId] = useState<number>()
        const [allResults, setAllResults] = useState<{
            [gasTypeId: string]: {
                result1: string
                result2: string
                size1: boolean
                size2: boolean
            }
        } | null>(null)

        useEffect(() => {
            if (auditId > 0) {
                handleChange(auditId)
            }
        }, [error, allResults])

        useEffect(() => {
            if (resCalculate?.detail) {
                const newResults: typeof allResults = {}
                Object.entries(resCalculate.detail).forEach(([gasTypeId, vals]) => {
                    const gasIdNum = parseInt(gasTypeId)
                    newResults[gasTypeId] = {
                        result1: formatResult(vals.result1, gasIdNum),
                        result2: formatResult(vals.result2, gasIdNum),
                        size1: typeof vals.result1 === 'number',
                        size2: typeof vals.result2 === 'number',
                    }
                })
                setAllResults(newResults)

                setTimeout(() => {
                    document
                        .getElementById('result_container')
                        ?.scrollIntoView({ behavior: 'smooth', block: 'start' })
                }, 150)
            }
        }, [resCalculate])

        const formatResult = (value: number | string, gasType: number): string => {
            if (typeof value === 'number') {
                let formatValue
                if (gasType > 1 && gasType < 4) {
                    formatValue = value.toFixed(3).replace('.', ',')
                } else {
                    formatValue = value.toFixed(1).replace('.', ',')
                }
                return formatValue
            }

            return value as string
        }

        const handleChange = (audit_id?: number) => {
            if ((auditId >= 0 || (audit_id && audit_id >= 0)) && !isLoadingAuditUpdate) {
                const allValues = calcForm.getFieldsValue() as CalculatorFormValues
                const auditData: CalculatorAuditValues = collectAudit(
                    allValues,
                    auditId >= 0 ? auditId : audit_id,
                )
                auditUpdate(auditData)
            }
        }

        const collectAudit = (
            allValues: CalculatorFormValues,
            audit_id?: number,
        ): CalculatorAuditValues => {
            const auditData: CalculatorAuditValues = {}
            if (audit_id && audit_id >= 0) {
                auditData.id = auditId
            } else if (auditId >= 0) {
                auditData.id = audit_id
            }
            if (allValues) {
                if (allValues.volume) {
                    auditData.volume = parseFloat(
                        allValues.volume?.replaceAll(' ', '')?.replace(',', '.'),
                    )
                }
                if (currentRegionId) {
                    auditData.region_id = currentRegionId
                }
                if (allValues.year) {
                    auditData.year_id = allValues.year
                }
                auditData.month_id = allValues.month ? allValues.month : -1
            }
            if (allResults) {
                auditData.results = Object.fromEntries(
                    Object.entries(allResults).map(([gasTypeId, val]) => [
                        gasTypeId,
                        { result1: val.result1, result2: val.result2 },
                    ]),
                )
            }
            if (error) {
                auditData.calculation_error = error
            }
            if (agentsData.length > 0) {
                auditData.green_tools = agentsData.map((agent) => ({
                    volume: parseFloat(agent.volume.replaceAll(' ', '').replace(',', '.')),
                    gen_type_id: agent.category,
                    coeff: agent.coeff,
                }))
            }
            return auditData
        }

        const onFinish = async (values: any, audit_id?: number) => {
            setLoading(true)
            setError('')
            try {
                if (!isFirstCalc && !audit_id) {
                    updateAuditId(values, (values, data) => {
                        onFinish(values, data?.detail?.id)
                    })
                    return
                }
                const requestData = {
                    volume: values.volume.replaceAll(' ', '').replace(',', '.'),
                    region_id: currentRegionId,
                    year_id: values.year,
                    month_id: values.month || -1,
                    agents: agentsData.map((value) => {
                        return {
                            volume: parseFloat(value.volume.replaceAll(' ', '').replace(',', '.')),
                            gen_type_id: value.category,
                            coeff: value.coeff,
                        }
                    }),
                    audit_id: audit_id ? audit_id : auditId,
                    locale: locale,
                }
                fetchCalculate(requestData, () => setIsFirstCalc(false))
            } catch (err) {
                setError(t('calculator.errors.calculation'))
                message.error(t('calculator.errors.calculation'))
            } finally {
                setLoading(false)
            }
        }

        const resetForm = () => {
            calcForm.resetFields(['volume', 'region', 'year', 'month'])
            resetAudit()
            setAllResults(null)
            setAgentsData([])
            setCurrentRegionId(undefined)
            setTimeout(() => {
                scrollToMain()
            }, 200)
        }

        useImperativeHandle(ref, () => ({
            resetForm,
        }))

        const formatVolume = (value: string) => {
            if (!value || value === '') {
                setAgentsData([])
                return
            }
            if (value[value.length - 1] === '.' || value[value.length - 1] === ',') {
                return
            }
            const numericValue = parseFloat(value.replaceAll(' ', '').replace(',', '.'))
            let agentsSum = 0
            agentsData.forEach(function (num) {
                agentsSum += parseFloat(num.volume.replaceAll(' ', '').replace(',', '.'))
            })
            if (numericValue < agentsSum) {
                setAgentsData([])
            }
            const formatted = formatNumber(value)

            calcForm.setFieldsValue({ volume: formatted })
        }

        async function downloadReport() {
            const allValues = calcForm.getFieldsValue() as CalculatorFormValues
            const requestData: CalcElectricityReportRequest = {
                volume: parseFloat(
                    (allValues.volume as string).replaceAll(' ', '').replace(',', '.'),
                ),
                region_id: parseInt(currentRegionId?.toString() as string),
                year_id: allValues.year as number,
                month:
                    data && allValues.month
                        ? data.months.find((month) => month.id === allValues.month)?.name
                        : undefined,
                green_tools: agentsData.map((agent) => ({
                    gen_type: agent.category,
                    volume: parseFloat(agent.volume.replaceAll(' ', '').replace(',', '.')),
                    coeff: parseFloat(agent.coeff.toString()),
                })),
                results: allResults
                    ? Object.entries(allResults).map(([gasTypeId, res]) => {
                          const gasType = data?.gas_types.find((g) => g.id.toString() === gasTypeId)
                          return {
                              gas_type_name: gasType ? gasType.name : `Газ ${gasTypeId}`,
                              result1: res.size1
                                  ? parseFloat(res.result1.split(' ')[0].replace(',', '.'))
                                  : undefined,
                              result2: res.size2
                                  ? parseFloat(res.result2.split(' ')[0].replace(',', '.'))
                                  : undefined,
                          }
                      })
                    : [],
            }
            const response = await request.post(
                '/api/extension/v1/files/calculator/electricity',
                requestData,
                { responseType: 'blob' },
            )
            const anchor = document.createElement('a')
            const disposition = response.headers['content-disposition']
            const filenameMatch = disposition?.match(/filename="?([^"]+)"?/)
            const filename = filenameMatch?.[1] || `electricity_report.pdf`
            const blob = new Blob([response.data], { type: 'application/pdf' })
            const objectUrl = window.URL.createObjectURL(blob)

            anchor.href = objectUrl
            anchor.download = decodeURIComponent(escape(filename))
            anchor.click()
            window.URL.revokeObjectURL(objectUrl)
            document.body.removeChild(anchor)
        }

        if (!data) {
            return <Spin spinning tip={t('calculator.loading')} />
        }
        return (
            <>
                <Form
                    form={calcForm}
                    layout="vertical"
                    onFinish={onFinish}
                    className={style.formWrap}
                >
                    <div className={style.calculatorFields}>
                        <div className={style.formDescription}>
                            {t('calculator.energyDescription')}
                        </div>
                        {/* Поле объема */}
                        <Form.Item
                            label={t('calculator.fields.volume.label')}
                            className={style.formItem}
                            name="volume"
                            rules={[
                                {
                                    required: true,
                                    message: t('calculator.fields.volume.error.required'),
                                },
                                {
                                    validator: (_, value) => {
                                        if (!value) {
                                            return Promise.reject()
                                        }
                                        const normalizedValue = value
                                            .replaceAll(' ', '')
                                            .replace(',', '.')
                                        return /^\d+(\.\d{1,3})?$/.test(normalizedValue)
                                            ? Promise.resolve()
                                            : Promise.reject(
                                                  t('calculator.fields.volume.error.format'),
                                              )
                                    },
                                },
                            ]}
                        >
                            <Input
                                onKeyDown={restrictInput}
                                onChange={(e) => formatVolume(e.target.value)}
                            />
                        </Form.Item>

                        {/* Поле региона */}
                        <Form.Item
                            className={style.formItem}
                            label={t('calculator.fields.region.label')}
                            name="region"
                            rules={[
                                {
                                    required: true,
                                    message: t('calculator.fields.region.error'),
                                },
                            ]}
                        >
                            <Select
                                showSearch
                                allowClear
                                onChange={(_, option) => {
                                    setCurrentRegionId((option as any).key)
                                }}
                                optionFilterProp="children"
                                optionLabelProp={'children'}
                            >
                                {data.regions.map((region: any) => (
                                    <Select.Option key={region.id} value={region.name}>
                                        {locale === 'ru' ? region.name : region.name_en}
                                    </Select.Option>
                                ))}
                            </Select>
                        </Form.Item>
                        {/* Календарный год */}
                        <Form.Item
                            className={style.formItem}
                            label={t('calculator.fields.year.label')}
                            name="year"
                            rules={[
                                {
                                    required: true,
                                    message: t('calculator.fields.year.error'),
                                },
                            ]}
                        >
                            <Select
                                showSearch
                                optionFilterProp="children"
                                allowClear
                                optionLabelProp={'children'}
                            >
                                {data.years.map((year: any) => (
                                    <Select.Option key={year.id} value={year.id}>
                                        {year.name}
                                    </Select.Option>
                                ))}
                            </Select>
                        </Form.Item>
                        {/* Месяц (необязательное) */}
                        <Form.Item
                            className={style.formItem}
                            label={
                                <span>
                                    {t('calculator.fields.month.label')}
                                    <Tooltip
                                        title={t('calculator.fields.month.tooltip')}
                                        overlayStyle={{ maxWidth: 300 }}
                                    >
                                        <QuestionCircleOutlined
                                            style={{
                                                marginLeft: 8,
                                                color: 'rgba(0, 0, 0, 0.45)',
                                            }}
                                        />
                                    </Tooltip>
                                </span>
                            }
                            name="month"
                        >
                            <Select
                                showSearch
                                allowClear
                                optionFilterProp="children"
                                optionLabelProp={'children'}
                            >
                                {data.months.map((month: any) => (
                                    <Select.Option key={month.id} value={month.id}>
                                        {month.name}
                                    </Select.Option>
                                ))}
                            </Select>
                        </Form.Item>
                        {/* Тип парникового газа - убран, теперь отображаются все типы */}
                        <button
                            className={style.primaryButtonCentered}
                            type="button"
                            onClick={() => setAgentsModalDisplay(true)}
                            style={{ padding: '8px 16px' }}
                        >
                            {t('calculator.modal.toggleTitle')}
                        </button>
                        <AgentsModal
                            isModalVisible={agentsModalDisplay}
                            setIsModalVisible={setAgentsModalDisplay}
                            calcForm={calcForm}
                            categories={data.categories}
                            agentsData={agentsData}
                            onSubmit={(values) => {
                                const agentData = {
                                    volume: values.agentVolume,
                                    category: values.categoryId,
                                    coeff: values.emissionCoeff,
                                }
                                setAgentsData((prev) => {
                                    return [...prev, agentData]
                                })
                            }}
                            locale={locale}
                        />
                        <div className={style.agentsGrid}>
                            {agentsData.map((agent, index) => {
                                const category = data.categories.find(
                                    (cat: any) => cat.id === agent.category,
                                )

                                return (
                                    <Alert
                                        key={index}
                                        className={style.agentAlert}
                                        message={
                                            <div className={style.alertTitle}>
                                                {`Объем: ${agent.volume} МВт·ч`}
                                            </div>
                                        }
                                        description={
                                            <>
                                                <CloseOutlined
                                                    className={style.alertCloseIcon}
                                                    onClick={() =>
                                                        setAgentsData((prev) =>
                                                            prev.filter((_, i) => i !== index),
                                                        )
                                                    }
                                                />
                                                <span className={style.alertDetail}>{`Категория: ${
                                                    locale === 'ru'
                                                        ? category?.name
                                                        : category?.name_en
                                                }`}</span>
                                                <Divider type="vertical" />
                                                <span
                                                    className={style.alertDetail}
                                                >{`Коэффициент: ${agent.coeff} кг/МВт·ч`}</span>
                                            </>
                                        }
                                        type="info"
                                    />
                                )
                            })}
                        </div>
                        <Form.Item>
                            <ButtonsBlock margin cancelAction={() => resetForm()} />
                        </Form.Item>
                    </div>
                    {error && <Text type="danger">{error}</Text>}
                </Form>
                {(allResults || loading) && (
                    <div className={style.resultContainer} id="result_container">
                        <div className={style.resultHead}>
                            {t('calculator.results.header.part1')}
                            <strong>{t('calculator.results.header.boldEnergy')}</strong>
                            {t('calculator.results.header.part2')}
                        </div>
                        {allResults && (
                            <>
                                <div className={style.resultText}>
                                    {t('calculator.results.regionalMethod')}
                                    <div className={style.resultLine}>
                                        {(() => {
                                            const errorText = Object.values(allResults).find(
                                                (r) => !r.size1,
                                            )?.result1
                                            return errorText ? (
                                                <Text className={style.resultString}>
                                                    {errorText}
                                                </Text>
                                            ) : null
                                        })()}
                                    </div>
                                    {data?.gas_types.map((gasType) => {
                                        const gasResult = allResults[String(gasType.id)]
                                        if (!gasResult || !gasResult.size1) {
                                            return null
                                        }
                                        const measure =
                                            gasType.id === data.gas_types[0].id
                                                ? t('calculator.results.measureShort')
                                                : t('calculator.results.measure')
                                        return (
                                            <div key={gasType.id} className={style.resultLine}>
                                                {locale === 'ru'
                                                    ? gasType.name
                                                    : (gasType as any).name_en ?? gasType.name}{' '}
                                                <div className={style.resultWrap}>
                                                    <Text className={style.resultNumeric}>
                                                        {gasResult.result1}
                                                    </Text>
                                                    <p className={style.measure}>{measure}</p>
                                                </div>
                                            </div>
                                        )
                                    })}
                                </div>
                                <div className={style.resultText}>
                                    {t('calculator.results.marketMethod')}
                                    <div className={style.resultLine}>
                                        {(() => {
                                            const errorText = Object.values(allResults).find(
                                                (r) => !r.size2,
                                            )?.result2
                                            return errorText ? (
                                                <Text className={style.resultString}>
                                                    {errorText}
                                                </Text>
                                            ) : null
                                        })()}
                                    </div>
                                    {data?.gas_types.map((gasType) => {
                                        const gasResult = allResults[String(gasType.id)]
                                        if (!gasResult || !gasResult.size2) {
                                            return null
                                        }
                                        const measure =
                                            gasType.id === data.gas_types[0].id
                                                ? t('calculator.results.measureShort')
                                                : t('calculator.results.measure')
                                        return (
                                            <div key={gasType.id} className={style.resultLine}>
                                                {locale === 'ru'
                                                    ? gasType.name
                                                    : (gasType as any).name_en ?? gasType.name}{' '}
                                                <div className={style.resultWrap}>
                                                    <Text className={style.resultNumeric}>
                                                        {gasResult.result2}
                                                    </Text>
                                                    <p className={style.measure}>{measure}</p>
                                                </div>
                                            </div>
                                        )
                                    })}
                                </div>
                                <button
                                    onClick={downloadReport}
                                    style={{ marginTop: '10px' }}
                                    className={style.primaryButton}
                                >
                                    {t('calculator.buttons.downloadReport')}
                                </button>
                            </>
                        )}
                    </div>
                )}
            </>
        )
    },
)
export default EnergyForm
